<template>
  <div class="m-prematch-summary">
    <div class="m-flex-item">
      <div class="m-tournament">
        <img
          v-if="event.categoryIcon"
          :src="event.categoryIcon"
          class="m-category-icon"
          loading="lazy"
        >
        <span class="category">{{ event.category }} -</span>
        <span class="tournament">{{ event.tournament }}</span>
      </div>

      <div class="m-meta">
        <span class="m-time">{{ formatDate(event.startTime, 'DD/MM/YYYY HH:mm') }}</span>
      </div>
    </div>

    <div
      class="m-flex-item m-teams-info"
      :class="{
        'abnormal': abnormalStatus
      }"
    >
      <div class="m-teams">
        <div
          class="m-team m-home-team"
          :class="{
            'lost': homeWonStatus === -1
          }"
        >
          {{ event.homeTeam }}
        </div>
        <div
          class="m-team m-away-team"
          :class="{
            'lost': homeWonStatus === 1
          }"
        >
          {{ event.awayTeam }}
        </div>
      </div>

      <div
        v-if="abnormalStatus"
        class="m-abnormal-status"
      >
        {{ abnormalStatus }}
      </div>

      <div
        v-else
        class="m-scores"
      >
        <template v-for="(item, index) in scores">
          <template v-if="index === 0">
            <div
              :key="index"
              class="m-score-item m-whole-score"
            >
              <div class="m-score m-score-label">
                {{ wholeScoreLabel }}
              </div>
              <div
                class="m-score m-home-score"
                :class="{
                  'lost': homeWonStatus === -1
                }"
              >
                {{ item[0] }}
              </div>
              <div
                class="m-score"
                :class="{
                  'lost': homeWonStatus === 1
                }"
              >
                {{ item[1] }}
              </div>
            </div>
          </template>

          <template v-else>
            <div
              :key="index"
              class="m-score-item"
            >
              <div class="m-score m-score-label">
                {{ isSoccer || isVSoccer ? 'HT' : index }}
              </div>
              <div
                class="m-score m-home-score"
                :class="{
                  'lost': +item[0] < +item[1]
                }"
              >
                {{ item[0] }}
              </div>
              <div
                class="m-score"
                :class="{
                  'lost': +item[0] > +item[1]
                }"
              >
                {{ item[1] }}
              </div>
            </div>
          </template>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { eventAbnormalStatus } from '@ms/utils/eventHandlers/event-stat'
import { formatDate } from '@ms/utils/format'

const getAllScores = (event = {}) => {
  const result = []
  const { scoreOfWholeMatch, scoreOfSection } = event

  // 整场的比分
  const wholeScores = (scoreOfWholeMatch || '-:-').split(':')

  result.push(wholeScores)

  // 节比分
  if (scoreOfSection) {
    let scores = scoreOfSection
    if (typeof scores === 'string') {
      scores = JSON.parse(scores)
    }
    const sectionScores = scores.map((x) => x.split(':'))
    if (event.sportId === 'sr:sport:1') {
      // soccer 只显示上半场比分
      result.push(sectionScores[0])
    } else {
      result.push(...sectionScores)
    }
  }

  // 不显示小节比分
  // 正常比分之外，最多显示 5 列，最多共 6 列
  return result.slice(0, 6)
}

export default {
  name: 'PrematchSummary',
  props: {
    event: {
      type: Object,
      default: () => ({})
    }
  },
  setup() {
    return {
      formatDate
    }
  },
  computed: {
    scores() {
      return getAllScores(this.event)
    },
    homeWonStatus() {
      const wholeScores = this.scores[0]
      const homeScore = +wholeScores[0]
      const awayScore = +wholeScores[1]
      if (homeScore > awayScore) {
        return 1
      } else if (homeScore < awayScore) {
        return -1
      } else {
        return 0
      }
    },
    isSoccer() {
      return this.event.sportId === 'sr:sport:1'
    },
    isVSoccer() {
      return this.event.sportId === 'lv:sport:1'
    },
    wholeScoreLabel() {
      return this.isSoccer || this.isVSoccer ? 'FT' : 'Score'
    },
    abnormalStatus() {
      return eventAbnormalStatus(this.event?.status)
    }
  }
}
</script>

<style lang="less" scoped>
@import url("~wap/common/style/variable.less");
@import url("~wap/common/style/mixin.less");

.m-prematch-summary {
  color: #999;
  font-size: 12px;
  line-height: 14/@rem;

  .m-flex-item {
    min-height: 40px;
    padding: 0 16px;
  }

  .m-flex-item,
  .m-scores {
    display: flex;
    justify-content: space-between;
  }

  .m-tournament {
    display: flex;
    flex: 1;
    align-items: center;

    .m-category-icon {
      width: 16px;
      height: 16px;
      border-radius: 50%;
      margin-right: 8px;
    }

    .category {
      color: #999;
    }

    .tournament {
      position: relative;
      width: 0;
      flex: 1;
      margin-left: 2px;
      color: #fff;
      text-decoration: underline;
      .ellipsis();
    }
  }

  .m-teams {
    width: 60%;
    flex: 1;
    color: #fff;
    font-size: 14/@rem;
    line-height: 18/@rem;

    .m-home-team {
      margin-bottom: 8px;
    }

    .m-team {
      width: 100%;
      .ellipsis();

      &.lost {
        color: #999;
      }
    }
  }

  .m-scores {
    flex: none;
  }

  .m-meta {
    display: flex;
    flex: none;
    align-items: center;
    margin-left: 10/@rem;
    color: #666;

    & > span,
    i {
      vertical-align: top;
    }
  }

  .m-teams-info {
    position: relative;
    align-items: flex-end;
    padding: 6px 16px 10px;
    border-radius: 0 0 20px 20px;
    background:#161616;

    &.abnormal {
      align-items: center;

      .m-teams {
        .m-team {
          color: #fff;
        }
      }
    }

    &::after {
      position: absolute;
      top: 27px;
      left: 16px;
      width: calc(100% - 32px);
      height: 1px;
      background: #333;
      content: '';
    }
  }

  .m-score-item {
    width: 16/@rem;
    margin-left: 10/@rem;
    color: #fff;
    font-size: 14/@rem;
    line-height: 18/@rem;
    text-align: center;

    &.m-whole-score {
      width: auto;
      margin-right: 10/@rem;
    }

    .m-score-label {
      color: #666;
      font-size: 12px;
      line-height: 16px;
    }

    .m-home-score {
      margin: 14px 0 8px;
    }

    .m-score {
      &.lost {
        color: #999;
      }
    }
  }

  .m-abnormal-status {
    color: #666;
    font-size: 14/@rem;
    line-height: 18/@rem;
  }
}
</style>
<style lang="scss" scoped>
[data-mode="light"] .m-prematch-summary {
  .m-flex-item {
    background:linear-gradient(180deg,#fff 0%,#f7f7f7 100%);
  }

  .m-teams {
    color: #000;
  }

  .m-score-item {
    color: #000;
  }

  .m-teams-info {
    background:#fff;

    .m-team.lost {
     color: #999;
     font-weight:500;
    }

    .m-score.lost {
      color: #999;
    }

    &::after {
      background: #ececec;
    }
  }

  .m-tournament {
    .category {
      color: #666;
    }

    .tournament {
      color: #000;
    }
  }
}
</style>